import { create, StateCreator } from 'zustand';
import * as EditorTypeAll from '@/components/editor/tinymce.d';
import { EmailStatus } from '@/types/email-unified.model';
import { mergeHtmlContent } from '@/hooks/useMergeHtmlContent';
/**
 * 附件上传进度状态
 */
export type UploadStatus = 'waiting' | 'uploading' | 'success' | 'failed';

/**
 * 附件模型
 */
export interface AttachmentsModel {
  nosKey: string;
  fileName: string;
  fileSize: number;
  type: string;
  downloadUrl: string;
  expireTime?: number;
  identity?: string;
  status?: UploadStatus;
  originFile?: File; // 附件原始数据，用来失败后重新上传
}

/**
 * 营销信编辑数据
 */
export interface CampaignEditData {
  msgId?: string;
  contactId: string;
  contentId?: string;
  subject: string;
  content: string;
  attachments: AttachmentsModel[];
  emailStatus?: EmailStatus;
}

/**
 * 邮件编辑器状态
 */
interface MailEditorState {
  /** 邮件主题 */
  subject: string;
  /** 邮件正文内容 */
  content: string;
  /** 引文内容（用于回复时暂存引文，点击引文缩略符号时追加到正文） */
  quotedContent: string;
  /** 附件列表 */
  attachments: AttachmentsModel[];
  /** 是否正在上传 */
  uploading: boolean;
  /** 是否正在自动保存 */
  isAutoSaving: boolean;
  /** 编辑器模式：dialog | window */
  editorMode: 'dialog' | 'window';
  /** 弹窗是否可见 */
  dialogVisible: boolean;

  // 营销信编辑对话框相关状态
  /** 营销信编辑对话框是否打开 */
  campaignDialogOpen: boolean;
  /** 营销信编辑数据 */
  campaignEditData: CampaignEditData | null;
  /** 主题高亮 */
  subjectHighlight: boolean;

  // 草稿编辑相关状态
  /** 草稿ID（往来邮件草稿） */
  draftId: string | null;
  /** 草稿关联的联系人ID */
  draftContactId: string | null;
  /** 内容更新次数 */
  contentUpdateCount: number;
  /** 回复邮件的收件人邮箱（从原邮件的 from 字段提取） */
  receiverEmail: string | null;
}

/**
 * 邮件编辑器操作
 */
interface MailEditorActions {
  /** 设置邮件主题 */
  setSubject: (subject: string) => void;
  /** 设置邮件内容 */
  setContent: (content: string) => void;
  /** 设置引文内容 */
  setQuotedContent: (quotedContent: string) => void;
  /** 清空引文内容 */
  clearQuotedContent: () => void;
  /** 将引文内容追加到正文尾部 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appendQuotedContentToEditor: (editorRef: any) => void;
  /** 设置附件列表 */
  setAttachments: (attachments: AttachmentsModel[]) => void;
  /** 添加附件 */
  addAttachment: (attachment: AttachmentsModel) => void;
  /** 删除附件 */
  removeAttachment: (identity: string) => void;
  /** 更新附件状态 */
  updateAttachment: (identity: string, updates: Partial<AttachmentsModel>) => void;
  /** 设置上传状态 */
  setUploading: (uploading: boolean) => void;
  /** 设置自动保存状态 */
  setIsAutoSaving: (isAutoSaving: boolean) => void;
  /** 切换编辑器模式 */
  setEditorMode: (mode: 'dialog' | 'window') => void;
  /** 设置弹窗可见性 */
  setDialogVisible: (visible: boolean) => void;
  /** 清空所有数据 */
  clearAll: () => void;
  /** 获取成功上传的附件 */
  getSuccessAttachments: () => AttachmentsModel[];

  // 营销信编辑对话框操作
  /** 打开营销信编辑对话框 */
  openCampaignDialog: (data: CampaignEditData) => void;
  /** 关闭营销信编辑对话框 */
  closeCampaignDialog: () => void;
  /** 更新营销信编辑数据 */
  updateCampaignEditData: (data: Partial<CampaignEditData>) => void;
  /** 设置主题高亮 */
  setSubjectHighlight: (highlight: boolean) => void;

  // 草稿编辑操作方法
  /** 设置草稿ID和联系人ID */
  setDraftId: (draftId: string | null, contactId: string | null) => void;
  /** 清空草稿ID */
  clearDraftId: () => void;
  /** 加载草稿到编辑器（从草稿箱加载） */
  loadDraftToEditor: (
    draftId: string,
    contactId: string,
    subject: string,
    content: string,
    attachments: AttachmentsModel[]
  ) => void;
  /** 设置内容更新次数 */
  setContentUpdateCount: (count: number) => void;
  /** 设置回复邮件的收件人邮箱 */
  setReceiverEmail: (email: string | null) => void;
}

type MailEditorStore = MailEditorState & MailEditorActions;

/**
 * 创建邮件编辑器状态切片
 */
const createMailEditorSlice: StateCreator<MailEditorStore, [], [], MailEditorStore> = (set, get) => ({
  // 初始状态
  subject: '',
  content: '',
  quotedContent: '',
  attachments: [],
  uploading: false,
  isAutoSaving: false,
  editorMode: 'window',
  dialogVisible: false,

  // 营销信编辑对话框初始状态
  campaignDialogOpen: false,
  campaignEditData: null,

  subjectHighlight: false,
  contentUpdateCount: 0,
  // 草稿编辑初始状态
  draftId: null,
  draftContactId: null,
  // 回复邮件收件人邮箱初始状态
  receiverEmail: null,
  // 操作方法
  setSubject: (subject: string) => set({ subject }),

  setContent: (content: string) => set({ content }),

  setQuotedContent: (quotedContent: string) => set({ quotedContent }),

  clearQuotedContent: () => set({ quotedContent: '' }),

  appendQuotedContentToEditor: (editorRef: React.RefObject<EditorTypeAll.Editor>) => {
    const { quotedContent } = get();
    if (quotedContent && editorRef?.current) {
      const currentContent = editorRef.current.getContent();
      const newContent = mergeHtmlContent(currentContent, quotedContent);
      set({ content: newContent, quotedContent: '', contentUpdateCount: get().contentUpdateCount + 1 });
    }
  },

  setAttachments: (attachments: AttachmentsModel[]) => set({ attachments }),

  addAttachment: (attachment: AttachmentsModel) =>
    set(state => ({
      attachments: [...state.attachments, attachment],
    })),

  removeAttachment: (identity: string) =>
    set(state => ({
      attachments: state.attachments.filter(a => a.identity !== identity),
    })),

  updateAttachment: (identity: string, updates: Partial<AttachmentsModel>) =>
    set(state => ({
      attachments: state.attachments.map(attachment =>
        attachment.identity === identity ? { ...attachment, ...updates } : attachment
      ),
    })),

  setUploading: (uploading: boolean) => set({ uploading }),

  setIsAutoSaving: (isAutoSaving: boolean) => set({ isAutoSaving }),

  setEditorMode: (mode: 'dialog' | 'window') => set({ editorMode: mode }),

  setDialogVisible: (visible: boolean) => set({ dialogVisible: visible }),

  setContentUpdateCount: (count: number) => set({ contentUpdateCount: count }),

  clearAll: () =>
    set({
      subject: '',
      content: '',
      quotedContent: '',
      attachments: [],
      uploading: false,
      isAutoSaving: false,
      draftId: null,
      draftContactId: null,
      contentUpdateCount: 0,
      receiverEmail: null,
    }),

  getSuccessAttachments: () => {
    const { attachments } = get();
    return attachments.filter(attachment => attachment.status === 'success');
  },

  // 营销信编辑对话框操作方法
  openCampaignDialog: (data: CampaignEditData) =>
    set({
      campaignDialogOpen: true,
      editorMode: 'dialog',
      campaignEditData: data,
    }),

  closeCampaignDialog: () =>
    set({
      editorMode: 'window',
      campaignDialogOpen: false,
      campaignEditData: null,
      attachments: [],
    }),

  updateCampaignEditData: (data: Partial<CampaignEditData>) =>
    set(state => ({
      campaignEditData: state.campaignEditData ? { ...state.campaignEditData, ...data } : null,
    })),
  setSubjectHighlight: (highlight: boolean) => set({ subjectHighlight: highlight }),

  // 草稿编辑操作方法
  setDraftId: (draftId: string | null, contactId: string | null) =>
    set({
      draftId,
      draftContactId: contactId,
    }),
  clearDraftId: () =>
    set({
      draftId: null,
      draftContactId: null,
    }),
  loadDraftToEditor: (
    draftId: string,
    contactId: string,
    subject: string,
    content: string,
    attachments: AttachmentsModel[]
  ) => {
    // 清空之前的草稿ID（如果存在）
    set({
      draftId: null,
      draftContactId: null,
    });
    // 设置新的草稿ID和内容
    set({
      draftId,
      draftContactId: contactId,
      subject,
      content,
      attachments,
      contentUpdateCount: get().contentUpdateCount + 1,
    });
  },
  setReceiverEmail: (email: string | null) => set({ receiverEmail: email }),
});

/**
 * 邮件编辑器状态管理 store
 */
export const useMailEditorStore = create<MailEditorStore>()(createMailEditorSlice);

export default useMailEditorStore;
